import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { usePaywall } from "@/contexts/PaywallContext";
import { Sparkles, X } from "lucide-react";
import { toast } from "sonner";

/** Soft, persona-voiced paywall. Triggered on 402 from any costly endpoint
 *  or manually via usePaywall().open({...}). */
export default function PaywallModal() {
  const { payload, close } = usePaywall();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(null); // "monthly" | "yearly" | null

  useEffect(() => {
    if (!payload) return;
    api
      .get("/billing/pricing")
      .then(({ data }) => setPricing(data))
      .catch(() => setPricing(null));
  }, [payload]);

  if (!payload) return null;

  const startCheckout = async (passKey) => {
    setLoading(passKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        pass_key: passKey,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
        setLoading(null);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(null);
    }
  };

  const heading =
    payload.status === "trial"
      ? "Get ahead of the wall"
      : "This is where I'd usually respond.";
  const body =
    payload.message ||
    "Welcome back. Get Colleague+ to keep going — your conversations are all here, waiting.";

  return (
    <div
      data-testid="paywall-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#2D2926]/50 backdrop-blur-sm"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FBF9F6] max-w-lg w-full rounded-3xl shadow-2xl border border-[#E6E2D8] overflow-hidden"
      >
        <div className="relative px-8 pt-8 pb-2">
          <button
            data-testid="paywall-close-btn"
            onClick={close}
            className="absolute top-5 right-5 text-[#9A938A] hover:text-[#2D2926] transition-colors"
            aria-label="Close"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-2 text-[#D4A574]">
            <Sparkles size={18} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.25em]">Colleague</span>
          </div>
          <h2 className="mt-3 font-serif text-3xl text-[#2D2926] leading-tight">
            {heading}
          </h2>
          <p className="mt-3 text-sm text-[#6B655D] leading-relaxed">{body}</p>
        </div>
        <div className="px-8 pb-8 pt-4 space-y-3">
          {/* Monthly */}
          <button
            type="button"
            data-testid="paywall-monthly-btn"
            disabled={!!loading}
            onClick={() => startCheckout("monthly")}
            className="group w-full text-left p-5 rounded-2xl border border-[#E6E2D8] bg-white hover:border-[#D4A574] hover:shadow-sm transition-all disabled:opacity-60"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-serif text-2xl text-[#2D2926]">
                $9.99
                <span className="text-sm text-[#6B655D] font-sans"> / month</span>
              </span>
              <span className="text-xs uppercase tracking-wider text-[#6B655D]">
                {loading === "monthly" ? "Loading…" : "Choose monthly →"}
              </span>
            </div>
            <p className="mt-1 text-xs text-[#6B655D]">
              30 days of full access. Pay again next month when you're ready.
            </p>
          </button>

          {/* Yearly */}
          <button
            type="button"
            data-testid="paywall-yearly-btn"
            disabled={!!loading}
            onClick={() => startCheckout("yearly")}
            className="group w-full text-left p-5 rounded-2xl border-2 border-[#D4A574] bg-gradient-to-br from-[#FBF6EE] to-white hover:shadow-md transition-all disabled:opacity-60 relative"
          >
            <span className="absolute -top-2.5 right-5 bg-[#D4A574] text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full">
              Save 16%
            </span>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-serif text-2xl text-[#2D2926]">
                $99
                <span className="text-sm text-[#6B655D] font-sans"> / year</span>
              </span>
              <span className="text-xs uppercase tracking-wider text-[#2D2926] font-medium">
                {loading === "yearly" ? "Loading…" : "Choose yearly →"}
              </span>
            </div>
            <p className="mt-1 text-xs text-[#6B655D]">
              Two months free. The friendliest way to commit.
            </p>
          </button>

          <div className="text-center pt-2">
            <button
              type="button"
              data-testid="paywall-not-now-btn"
              onClick={() => {
                close();
                navigate("/dashboard");
              }}
              className="text-xs text-[#9A938A] hover:text-[#6B655D] transition-colors"
            >
              Maybe next month
            </button>
          </div>

          {pricing?.features_unlocked && (
            <details className="pt-2">
              <summary className="text-xs text-[#9A938A] cursor-pointer hover:text-[#6B655D] transition-colors">
                What's included
              </summary>
              <ul className="mt-2 text-xs text-[#6B655D] space-y-1 pl-3">
                {pricing.features_unlocked.map((f, i) => (
                  <li key={i}>• {f}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
